import { combineReducers, configureStore, ConfigureStoreOptions } from '@reduxjs/toolkit';
import { persistStore } from 'redux-persist';

import PAGES from '@/constants/pages';
import {
  benefitsChoiceActions,
  benefitsChoiceReducer,
  benefitsChoiceSelectors,
} from '@/features/benefitsChoice';
import { carsApi, carsReducer, carsSelectors } from '@/features/cars';
import {
  choiceActions,
  choiceMiddleware,
  choiceReducer,
  choiceSelectors,
} from '@/features/choice';
import {
  comparisonActions,
  comparisonMiddleware,
  comparisonReducer,
  comparisonSelectors,
} from '@/features/comparison';
import { configReducer, configSelectors } from '@/features/config';
import { creditActions, creditReducer, creditSelectors } from '@/features/credit';
import { dealersApi, dealersReducer } from '@/features/dealers';
import dictionariesReducer, { dictionariesApi } from '@/features/dictionaries/reducer';
import {
  equipmentsExpandedActions,
  equipmentsExpandedReducer,
  equipmentsExpandedSelectors,
} from '@/features/equipments';
import {
  favoritesActions,
  favoritesReducer,
  favoritesSelectors,
} from '@/features/favorites';
import { filtersApi, filtersReducer, filtersSelectors } from '@/features/filters';
import {
  installationActions,
  installationMiddleware,
  installationReducer,
  installationSelectors,
} from '@/features/installation';
import {
  referencesActions,
  referencesReducer,
  referencesSelectors,
} from '@/features/references';
import { tradeInActions, tradeInReducer, tradeInSelectors } from '@/features/tradeIn';
import { ConstructorEnum } from '@/interfaces/constructor.interfaces';
import deepCopy from '@/utils/deepCopy';
import gridUtils from '@/utils/gridUtils';
import instanceOfGrid from '@/utils/instanceOfGrid';
import mergeDeep from '@/utils/mergeDeep';

export type AppStoreType = ReturnType<typeof setupStore>['store'];
export type AppDispatchType = AppStoreType['dispatch'];
export type RootStateType = ReturnType<typeof rootReducer>;

export const rootQueriesApi = {
  cars: carsApi,
  dealers: dealersApi,
  filters: filtersApi,
};

export const rootActions = {
  choice: choiceActions,
  credit: creditActions,
  references: referencesActions,
  benefitsChoice: benefitsChoiceActions,
  equipmentsExpanded: equipmentsExpandedActions,
  installation: installationActions,
  comparison: comparisonActions,
  favorites: favoritesActions,
  tradeIn: tradeInActions,
};

export const rootSelectors = {
  cars: carsSelectors,
  filters: filtersSelectors,
  choice: choiceSelectors,
  credit: creditSelectors,
  references: referencesSelectors,
  config: configSelectors,
  benefitsChoice: benefitsChoiceSelectors,
  equipmentsExpanded: equipmentsExpandedSelectors,
  installation: installationSelectors,
  comparison: comparisonSelectors,
  favorites: favoritesSelectors,
  tradeIn: tradeInSelectors,
};

export const rootMiddleware = [
  ...choiceMiddleware,
  ...installationMiddleware,
  ...comparisonMiddleware,
];

const rootReducer = combineReducers({
  choice: choiceReducer,
  config: configReducer,
  credit: creditReducer,
  references: referencesReducer,
  benefitsChoice: benefitsChoiceReducer,
  equipmentsExpanded: equipmentsExpandedReducer,
  installation: installationReducer,
  comparison: comparisonReducer,
  favorites: favoritesReducer,
  tradeIn: tradeInReducer,
  [dictionariesApi.reducerPath]: dictionariesReducer,
  [filtersApi.reducerPath]: filtersReducer,
  [carsApi.reducerPath]: carsReducer,
  [dealersApi.reducerPath]: dealersReducer,
});

const getStoreConfigurationOptions = (
  devTools = true,
): ConfigureStoreOptions<RootStateType> => {
  return {
    devTools,
    reducer: rootReducer,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({ serializableCheck: false })
        .concat(rootMiddleware)
        .concat(filtersApi.middleware)
        .concat(carsApi.middleware)
        .concat(dealersApi.middleware)
        .concat(dictionariesApi.middleware),
  };
};

export const setupStore = (preloadedState?: Partial<RootStateType>) => {
  const store = configureStore({
    ...getStoreConfigurationOptions(),
    preloadedState: preloadedState as RootStateType,
  });

  const persistor = persistStore(store);

  return {
    store,
    persistor,
  };
};

/**
 * Получить начальное состояние приложения
 * @param preloadedState Начальное состояние стора из конфигурации при инициализации приложения
 * @returns Состояние приложении на основе начального состояния приложения
 * и состояния указанного при инициализации приложения
 */
export const formatPreloadedState = (preloadedState: Partial<RootStateType>) => {
  const storeWithoutPreloadedState = configureStore(getStoreConfigurationOptions(false));
  const initialState = deepCopy(storeWithoutPreloadedState.getState());

  const { config } = preloadedState;

  /**
   * В случае принудительного скрытия отображения цены в фильтре при его наличии в конфигурации,
   * а также на странице выбора моделей при её использовании,
   * если свойство useAggregatePrice в секции настроек models установлено в true,
   * запроса на получение минимальной стоимости авто по моделям из стока авто не должно происходить.
   * https://tracker.yandex.ru/AUTO-2493
   */

  if (config && config.constructor) {
    const modelFilters =
      Object.prototype.hasOwnProperty.call(config.constructor, PAGES.Cars) &&
      gridUtils.findByCondition(
        config.constructor[PAGES.Cars],
        el => instanceOfGrid(el) && el.type === ConstructorEnum.FilterModelCheckboxGroup,
      );

    const modelList =
      Object.prototype.hasOwnProperty.call(config.constructor, PAGES.Models) &&
      gridUtils.findByCondition(
        config.constructor[PAGES.Models],
        el => instanceOfGrid(el) && el.type === ConstructorEnum.ModelList,
      );

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (!modelList?.props?.hasPrice && !modelFilters?.props?.hasPrice) {
      initialState.config.models = {
        ...initialState.config.models,
        useAggregatePrice: false,
      };
    }
  }

  if (config?.benefits?.enable) {
    initialState.config.sort = {
      ...initialState.config.sort,
      initialValues: ['minPrice'],
      items: initialState.config.sort.items.map(item => {
        if (item.key === 'price') {
          return {
            ...item,
            key: 'minPrice',
          };
        }

        if (item.key === '-price') {
          return {
            ...item,
            key: '-minPrice',
          };
        }

        return item;
      }),
    };
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return mergeDeep(initialState, preloadedState) as RootStateType;
};
